import { Fragment } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { offlineLayerEnabled } from "@/lib/offline/offlineFetch";
import { isOnlineNow } from "@/lib/offline/net";
import { Capacitor } from "@capacitor/core";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ModuleSessionProvider } from "@/contexts/ModuleSessionContext";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";
import { platform } from "@/platform/runtime";
import { PRODUCTS } from "@/platform/products";
import { ProductBoundary } from "@/platform/ProductBoundary";
import { SupportAccessGate, SupportSessionBanner } from "@/platform/supportAccess";
import type { ProductRoutesModule } from "@/products/types";

// Pagine condivise da tutti i prodotti (pubbliche)
import AppPrivacy from "./pages/legal/AppPrivacy";
import AccountDeletion from "./pages/legal/AccountDeletion";
import ResetPassword from "./pages/auth/ResetPassword";
import BankLinkCallback from "./pages/auth/BankLinkCallback";
import OnboardingEntryPage from "./pages/onboarding/OnboardingEntryPage";
import OnboardingTermsPage from "./pages/onboarding/OnboardingTermsPage";
import OnboardingPaymentPage from "./pages/onboarding/OnboardingPaymentPage";
import OnboardingCallPage from "./pages/onboarding/OnboardingCallPage";
import OnboardingCompletedPage from "./pages/onboarding/OnboardingCompletedPage";

// Offline-first (app nativa / flag di collaudo): le query partono sempre — senza rete rispondono dalla copia locale
// invece di restare «in pausa» — e non ritentano inutilmente quando manca la rete.
const OFFLINE_FIRST = offlineLayerEnabled();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — data stays fresh, no redundant fetches
      refetchOnWindowFocus: true, // re-fetch when user tabs back
      refetchOnReconnect: true, // re-fetch after network recovery
      retry: OFFLINE_FIRST ? (failureCount: number) => isOnlineNow() && failureCount < 2 : 2,
      ...(OFFLINE_FIRST ? { networkMode: "always" as const } : {}),
    },
    ...(OFFLINE_FIRST ? { mutations: { networkMode: "always" as const } } : {}),
  },
});

// Inner component to use hooks inside providers
function AppVersionWatcher() {
  const { updateAvailable, applyUpdate } = useAppVersionCheck();

  if (!updateAvailable) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "1rem",
        right: "1rem",
        zIndex: 9999,
        background: "#1e40af",
        color: "#fff",
        padding: "0.75rem 1.25rem",
        borderRadius: "0.5rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        fontSize: "0.875rem",
      }}
    >
      <span>Nuova versione disponibile</span>
      <button
        onClick={applyUpdate}
        style={{
          background: "#fff",
          color: "#1e40af",
          border: "none",
          borderRadius: "0.375rem",
          padding: "0.25rem 0.75rem",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "0.8rem",
        }}
      >
        Aggiorna
      </button>
    </div>
  );
}

/**
 * Pagina iniziale: sul dominio di un prodotto (o nell'app nativa) il suo accesso;
 * sull'host tutto-in-uno resta il comportamento storico.
 */
const ROOT_PATH = platform.product
  ? PRODUCTS[platform.product].loginPath
  : Capacitor.isNativePlatform()
    ? PRODUCTS.construction.loginPath
    : PRODUCTS.console.loginPath;

/**
 * Shell comune a tutti i prodotti. Le route arrivano dai prodotti montati su questo host
 * (src/products/<prodotto>/routes.tsx, caricati da src/products/loader.ts in main.tsx).
 */
const App = ({ products }: { products: ProductRoutesModule[] }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ModuleSessionProvider>
          {products.map(({ Globals }, i) => (Globals ? <Globals key={i} /> : null))}
          <ImpersonationProvider>
            <ThemeProvider>
              <ImpersonationBanner />
              <AppVersionWatcher />
              <SupportSessionBanner />
              <SupportAccessGate>
              <Routes>
                {/* Root redirect */}
                <Route path="/" element={<Navigate to={ROOT_PATH} replace />} />

                {/* Informativa privacy pubblica (URL richiesto da App Store) */}
                <Route path="/privacy" element={<AppPrivacy />} />
                {/* Eliminazione account: URL richiesto da Google Play (Sicurezza dei dati) */}
                <Route path="/eliminazione-account" element={<AccountDeletion />} />

                {/* Nuova password dal link di recupero (tutti i prodotti) */}
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                {/* Ritorno dalla banca dopo il consenso open banking (tutti i prodotti) */}
                <Route path="/auth/banca" element={<BankLinkCallback />} />

                {/* Funnel onboarding pubblico (T&C → pagamento → login) */}
                <Route path="/onboarding/:token" element={<OnboardingEntryPage />} />
                <Route path="/onboarding/:token/termini" element={<OnboardingTermsPage />} />
                <Route path="/onboarding/:token/pagamento" element={<OnboardingPaymentPage />} />
                <Route path="/onboarding/:token/call" element={<OnboardingCallPage />} />
                <Route path="/onboarding/:token/completato" element={<OnboardingCompletedPage />} />

                {/* Route dei prodotti montati su questo host */}
                {products.map((p, i) => <Fragment key={i}>{p.routes()}</Fragment>)}

                {/* Percorsi di altri prodotti → loro dominio; altrimenti 404 */}
                <Route path="*" element={<ProductBoundary />} />
              </Routes>
              </SupportAccessGate>
            </ThemeProvider>
          </ImpersonationProvider>
        </ModuleSessionProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
